import { useState, useEffect, useCallback, useRef } from 'react';
import { Text } from '@qwickapps/react-framework';
import { config } from '../../config/runtime';

interface WorkflowRun {
  id: number;
  name: string;
  head_branch: string;
  status: 'queued' | 'in_progress' | 'completed';
  conclusion: 'success' | 'failure' | 'cancelled' | 'skipped' | null;
  created_at: string;
  html_url: string;
}

const REFRESH_INTERVAL_MS = 60_000;

const CONCLUSION_COLORS: Record<string, string> = {
  success: '#4caf50',
  failure: '#f44336',
  cancelled: '#9e9e9e',
  skipped: '#9e9e9e',
};

const STATUS_COLORS: Record<string, string> = {
  queued: '#ff9800',
  in_progress: '#2196f3',
  completed: '#9e9e9e',
};

function formatRelativeTime(isoString: string): string {
  const diffSec = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function CICDStatusPanel() {
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isMounted = useRef(true);

  const fetchRuns = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setIsRefreshing(true);
    try {
      const res = await fetch(
        '/api/github/actions/runs?repo=raajkumars/qwickapps&per_page=20',
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { configured: boolean; workflow_runs?: WorkflowRun[] };
      if (!isMounted.current) return;
      setRuns(data.workflow_runs ?? []);
      setError(null);
    } catch {
      if (!isMounted.current) return;
      setError('Unable to fetch CI/CD runs');
    } finally {
      if (isMounted.current) {
        setLoading(false);
        if (showRefreshing) setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchRuns(false);
    const interval = setInterval(() => fetchRuns(true), REFRESH_INTERVAL_MS);
    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [fetchRuns]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Text variant="h5" content="CI/CD Status" gutterBottom />
        {isRefreshing && (
          <div
            role="status"
            aria-label="refreshing"
            style={{
              width: 12,
              height: 12,
              border: '2px solid var(--theme-border-color)',
              borderTopColor: 'var(--theme-color-primary)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        )}
      </div>
      <Text
        variant="body2"
        content="Recent workflow runs for raajkumars/qwickapps"
        customColor="var(--theme-text-secondary)"
        gutterBottom
      />

      {loading && <Text variant="body2" content="Loading..." customColor="var(--theme-text-secondary)" />}

      {error && (
        <div
          style={{
            padding: 12,
            borderRadius: 4,
            background: 'rgba(244,67,54,0.08)',
            border: '1px solid #f44336',
            marginBottom: 16,
          }}
        >
          <Text variant="body2" content={error} customColor="#f44336" />
        </div>
      )}

      {!loading && !error && runs.length === 0 && (
        <Text variant="body2" content="No workflow runs found." customColor="var(--theme-text-secondary)" />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {runs.map((run) => {
          const isFailure = run.conclusion === 'failure';
          return (
            <a
              key={run.id}
              href={run.html_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 6,
                  background: isFailure ? 'rgba(244,67,54,0.06)' : 'var(--theme-bg-surface)',
                  border: `1px solid ${isFailure ? '#f44336' : 'var(--theme-border-color)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text variant="body2" content={run.name} />
                  <Text
                    variant="caption"
                    content={`Branch: ${run.head_branch}`}
                    customColor="var(--theme-text-secondary)"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 8,
                      background: STATUS_COLORS[run.status] ?? '#9e9e9e',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {run.status}
                  </span>
                  {run.conclusion && (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 8,
                        background: CONCLUSION_COLORS[run.conclusion] ?? '#9e9e9e',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    >
                      {run.conclusion}
                    </span>
                  )}
                  <span
                    style={{ fontSize: 11, color: 'var(--theme-text-disabled)', whiteSpace: 'nowrap' }}
                  >
                    {formatRelativeTime(run.created_at)}
                  </span>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
